import { Controller, useFormContext } from "react-hook-form";
import { TextField } from "~/common/mui/TextField/TextField";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useTranslation } from "react-i18next";
import type { MediaFilterData, MediaFormFields } from "~/features/mediaLibrary/types/mediaLibraryFormTypes";
import { memo, useEffect, useMemo } from "react";
import classes from "./MediaLibrarySearchBox.module.less";
import { type SxProps, type Theme } from "~/common/mui";
import { debounce } from "lodash";
import SortByFilter from "../SortByFilter/SortByFilter";
import MediaLibraryFilters from "./MediaLibraryFilters";
import { trackMediaLibraryFilterActions } from "~/app/analytics/ga/mediaLibrary";

export const searchInputStyles: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
};

const inputSlotProps = {
  input: {
    startAdornment: (
      <div className={classes.searchIcon}>
        <Icon type={IconType.Search} />
      </div>
    ),
  },
};

const MediaLibrarySearchBox = memo(function MediaLibrarySearchBox(props: {
  filterOptions: MediaFilterData;
  submitForm: (data: MediaFormFields) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { submitForm, filterOptions } = props;

  const { control, handleSubmit, subscribe } = useFormContext<MediaFormFields>();

  const debouncedSubmit = useMemo(() => debounce(() => handleSubmit(submitForm)(), 500), [handleSubmit, submitForm]);

  useEffect(() => {
    const subscriptions = [
      "mediaTypes",
      "productPortfolios",
      "languages",
      "segments",
      "fetchFavourite",
      "sortOrder",
    ].map((name) =>
      subscribe({
        name: [name as keyof MediaFormFields],
        formState: { values: true },
        callback: ({ values }) => {
          const key = name as keyof MediaFormFields;
          trackMediaLibraryFilterActions(values?.q, key, values[key] as string);
          debouncedSubmit.cancel();
          handleSubmit(submitForm)();
        },
      }),
    );
    return () => {
      subscriptions.forEach((subscription) => subscription());
    };
  }, [subscribe, debouncedSubmit, handleSubmit, submitForm]);

  return (
    <form
      className={classes.container}
      onSubmit={handleSubmit((data) => {
        debouncedSubmit.cancel();
        const el = document.activeElement;
        if (el && "blur" in el) {
          (el as HTMLElement).blur();
        }
        submitForm(data);
      })}
    >
      <div className={classes.searchBar}>
        <button type="submit" hidden />
        <Controller
          name="q"
          control={control}
          render={({ field: { value, onChange } }) => (
            <TextField
              placeholder={t("generic.button.search")}
              className={classes.searchInput}
              sx={searchInputStyles}
              slotProps={inputSlotProps}
              value={value}
              onChange={(e) => {
                onChange(e);
                debouncedSubmit();
              }}
            />
          )}
        />
        <SortByFilter />
      </div>
      <MediaLibraryFilters filterOptions={filterOptions} />
    </form>
  );
});

export default MediaLibrarySearchBox;
